import React, { ComponentProps, useEffect } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { Alert, AlertActionButton, AlertActions, AlertText, AlertTitle } from '@fiverr-private/feedback';
import { Gql } from '@fiverr-private/dynamic_matching';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { AppI18n } from '../../i18n';
import { useGetCurrentFlow } from '../../hooks';
import { CustomSlideFade } from '../Animations';
import { briefCreationClickAction, briefCreationDrawerEducationIsShown } from '../../monitoring';
import { Step } from '../../constants';

const animationProps = {
  easing: 'easeIn',
  delay: 'moderate1',
  duration: 'slow2',
} as Partial<Omit<ComponentProps<typeof CustomSlideFade>, 'offsetX' | 'offsetY'>>;

export const RncAlert = () => {
  const {
    buyerType,
    missingFieldsCount,
    source,
    contextId,
    briefClassification,
    variant,
    parentElement,
    inJpHighSpendExperience,
  } = useBriefCreationContext();
  const flow = useGetCurrentFlow();
  const [isAuction, setIsAuction] = React.useState(flow === Gql.DmMatchCandidateCreationFlow.Auction);
  const isRncBuyer = buyerType === Gql.BuyerType.RegisteredNotConverted;
  const [isEligibleForRncFlow, setIsEligibleForRncFlow] = React.useState(
    isAuction && isRncBuyer && !missingFieldsCount && !inJpHighSpendExperience
  );

  const [isOpen, setIsOpen] = React.useState(isEligibleForRncFlow);
  const [doNotShowAgain, setDoNotShowAgain] = React.useState(false);

  useEffect(() => {
    setIsOpen(isEligibleForRncFlow);
  }, [isEligibleForRncFlow]);

  useEffect(() => {
    setIsAuction(flow === Gql.DmMatchCandidateCreationFlow.Auction);
  }, [flow]);

  useEffect(() => {
    setIsEligibleForRncFlow(isAuction && isRncBuyer && missingFieldsCount === 0 && !inJpHighSpendExperience);
  }, [isAuction, isRncBuyer, missingFieldsCount, inJpHighSpendExperience]);

  useEffect(() => {
    if (isOpen && !doNotShowAgain) {
      const leafClassification = briefClassification?.leaf_classification[0];

      briefCreationDrawerEducationIsShown({
        educationType: 'choose_routing_alert',
        categoryId: leafClassification?.category_id,
        subCategoryId: leafClassification?.sub_category_id,
        nestedCategoryId: leafClassification?.nested_sub_category_id,
        briefContextId: contextId,
        eventSource: source || '',
        variant,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, doNotShowAgain]);

  const closeAlert = () => {
    setIsOpen(false);
    setDoNotShowAgain(true);

    briefCreationClickAction({
      step: Step.Edit,
      action: 'got_it',
      briefContextId: contextId,
      eventSource: source || '',
      variant,
      parentElement,
    });
  };

  return isOpen && !doNotShowAgain ? (
    <CustomSlideFade show offsetY={20} {...animationProps}>
      <Container padding="4">
        <Alert variant="info">
          <AlertTitle>
            <AppI18n k="routing.rnc_alert.title" />
          </AlertTitle>
          <AlertText>
            <AppI18n k="routing.rnc_alert.description" />
          </AlertText>
          <AlertActions>
            <AlertActionButton onClick={closeAlert} intent="secondary">
              <AppI18n k="routing.rnc_alert.got_it" />
            </AlertActionButton>
          </AlertActions>
        </Alert>
      </Container>
    </CustomSlideFade>
  ) : null;
};
